<template>
  <div class="h-full bg-[#f8fafc] p-4 overflow-y-auto custom-scroll">
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] transition-all overflow-hidden mb-6">
      <div class="px-6 pt-5 pb-3">
        <h2 class="text-base font-bold text-[#0d4169]">Consumption Report</h2>
      </div>

      <form @submit.prevent="generateReport" class="px-6 pb-6 pt-2 border-b border-slate-100">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">
              Start Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="filters.startDate"
              required
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Date</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Report Type</label>
            <select
              v-model="filters.reportType"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option value="Summary">Summary</option>
              <option value="Detail">Detail</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Department</label>
            <select
              v-model="filters.departmentId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option disabled selected :value="null">Select Department</option>
              <option v-for="dep in departments" :key="dep.id" :value="dep.id">
                {{ dep.name }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left lg:col-span-2">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Search By Item</label>
            <input
              v-model="filters.searchItem"
              type="text"
              placeholder="Enter ingredient name..."
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>
        </div>

        <div class="mt-6 flex justify-between items-center gap-4">
          <button
            type="button"
            @click="handlePrintReport"
            class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="loading"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9V2h12v7"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span>Print</span>
          </button>
          <button
            type="submit"
            class="px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="!filters.startDate || loading"
          >
            <span v-if="loading" class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
            <span>{{ loading ? '' : 'Generate' }}</span>
          </button>
        </div>
      </form>
    </div>

    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden pr-3 pl-3 print-container">
      <div class="hidden print:block mb-6">
        <div class="print-doc-header print-doc-header-grid">
          <div class="print-doc-left">
            <div class="print-company-block">
              <img
                v-if="printCompanyLogo"
                :src="printCompanyLogo"
                :alt="printCompanyName || 'Company Logo'"
                class="print-company-logo"
              />
              <div v-else class="print-company-logo-fallback">Logo</div>
            </div>
          </div>
          <div class="print-doc-title-wrap">
            <div class="print-company-name print-company-name-title">{{ printCompanyName }}</div>
            <h1 class="print-doc-title text-xl font-bold text-center mb-2">Consumption Report</h1>
          </div>
          <div class="print-doc-meta print-doc-right">
            <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
          </div>
        </div>
        <hr class="my-4 border-black" />
      </div>
      <div class="relative overflow-auto min-h-[220px] max-h-[calc(100vh-260px)]">
        <table class="w-full consumption-table min-w-max print-table">
          <thead>
            <tr>
              <th class="w-10 text-center"><span class="print-long">No.</span><span class="print-short">No</span></th>
              <th
                v-if="filters.reportType === 'Detail'"
                class="w-24 text-center"
              >
                <span class="print-long">Date</span><span class="print-short">Dt</span>
              </th>
              <th class="min-w-[120px] text-left sticky-ingredient">
                <span class="print-long">Ingredient Name</span><span class="print-short">Item</span>
              </th>
              <th class="w-20 text-center"><span class="print-long">Bbf/Bignin/</span><span class="print-short">BBF</span></th>
              <th class="w-20 text-center"><span class="print-long">R.S. Quantity</span><span class="print-short">RS</span></th>
              <th class="w-20 text-center"><span class="print-long">R.N.S. Quantity</span><span class="print-short">RNS</span></th>
              <th class="w-20 text-center"><span class="print-long">Product</span><span class="print-short">Prod</span></th>
              <th class="w-24 text-center"><span class="print-long">Transfer Recived (T)</span><span class="print-short">TR(T)</span></th>
              <th class="w-20 text-center"><span class="print-long">Wastage</span><span class="print-short">Wstg</span></th>
              <th class="w-24 text-center font-bold bg-slate-100"><span class="print-long">Total On Hand</span><span class="print-short">TOH</span></th>
              <th class="w-24 text-center"><span class="print-long">Sales Manwale</span><span class="print-short">SM</span></th>
              <th class="w-24 text-center"><span class="print-long">Sales System</span><span class="print-short">SS</span></th>
              <th class="w-20 text-center"><span class="print-long">Managment</span><span class="print-short">Mgmt</span></th>
              <th class="w-20 text-center"><span class="print-long">Room</span><span class="print-short">Room</span></th>
              <th class="w-20 text-center"><span class="print-long">Promotion</span><span class="print-short">Promo</span></th>
              <th class="w-24 text-center"><span class="print-long">Company Cost</span><span class="print-short">CoC</span></th>
              <th class="w-20 text-center"><span class="print-long">Spoilage</span><span class="print-short">Splg</span></th>
              <th class="w-20 text-center"><span class="print-long">Other</span><span class="print-short">Oth</span></th>
              <th class="w-24 text-center"><span class="print-long">Transfer Issu (T)</span><span class="print-short">TI(T)</span></th>
              <th class="w-24 text-center"><span class="print-long">Spoiled By Items</span><span class="print-short">SBI</span></th>
              <th class="w-24 text-center"><span class="print-long">Other/Catering</span><span class="print-short">OC</span></th>
              <th class="w-24 text-center font-bold bg-blue-50"><span class="print-long">Total Active</span><span class="print-short">TA</span></th>
              <th class="w-20 text-center"><span class="print-long">Balance</span><span class="print-short">Bal</span></th>
              <th class="w-20 text-center"><span class="print-long">Ending</span><span class="print-short">End</span></th>
              <th class="w-20 text-center font-bold bg-red-50 text-red-700"><span class="print-long">Deference</span><span class="print-short">Diff</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="rows.length === 0">
              <td :colspan="filters.reportType === 'Detail' ? 26 : 25" class="px-4 py-24 text-center text-slate-400 italic text-sm">
                <div class="text-5xl mb-4 opacity-20">📄</div>
                No consumption records to display.<br />
                Select filters and click <span class="text-[#0d4169] font-bold">"Generate"</span>
              </td>
            </tr>
            <tr
              v-for="(row, index) in rows"
              :key="rowKey(row, index)"
              class="border-b last:border-0 hover:bg-slate-50 transition-colors"
              :class="row.type === 'product' ? 'bg-emerald-50/40' : ''"
            >
              <td class="text-center">{{ index + 1 }}</td>
              <td v-if="filters.reportType === 'Detail'" class="bg-slate-50/50 text-slate-600 text-sm text-center">
                {{ row.calendar || '---' }}
              </td>
              <td
                class="font-medium text-left sticky-ingredient"
                :class="row.type === 'product' ? 'text-emerald-800' : ''"
              >
                {{ row.ingredient }}
              </td>

              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.bbf) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.storeRecv) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.nonStock) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.production) }}</td>
              <td class="bg-emerald-50/30 text-emerald-700 text-sm font-bold text-center">{{ formatQty(row.transInT) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.wastage) }}</td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ formatQty(row.totalOnHand) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.salesManual) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.salesSystem) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.management) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.room) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.promotion) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.companyCost) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.spoilage) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.other) }}</td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ formatQty(row.transOutT) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.spoiledByItems) }}</td>
              <td class="bg-slate-50/50 text-slate-600 text-sm text-center">{{ formatQty(row.otherCatering) }}</td>
              <td class="font-black text-[#0d4169] text-center bg-slate-50/30">{{ formatQty(row.totalActive) }}</td>
              <td class="font-black text-green-700 text-center text-slate-500">{{ formatQty(row.balance) }}</td>
              <td class="bg-slate-50/50 text-slate-500 text-sm text-center">{{ formatQty(row.ending) }}</td>
              <td class="font-black text-center bg-red-50/30" :class="Number(row.deference) < 0 ? 'text-red-500' : (Number(row.deference) > 0 ? 'text-green-500' : '')">{{ formatQty(row.deference) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import store from '../../../store'
import { apiFetch } from '../../../utils/api'

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

const departments = ref([])
const rows = ref([])
const loading = ref(false)

const filters = ref({
  startDate: '',
  endDate: '',
  reportType: 'Summary',
  departmentId: null,
  searchItem: '',
})

// function toLocalISODate(date = new Date()) {
//   const yyyy = date.getFullYear()
//   const mm = String(date.getMonth() + 1).padStart(2, '0')
//   const dd = String(date.getDate()).padStart(2, '0')
//   return `${yyyy}-${mm}-${dd}`
// }

function formatQty(value) {
  const n = Number(String(value ?? 0).replace(/,/g, ''))
  const x = Number.isFinite(n) ? n : 0
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function rowKey(row, index) {
  if (filters.value.reportType === 'Detail') {
    return `${row.calendar || ''}-${row.type || ''}-${row.ingredientId || row.itemId || row.productId || row.ingredient}-${index}`
  }
  return `${row.type || ''}-${row.ingredientId || row.itemId || row.productId || row.ingredient}-${index}`
}

function handlePrintReport() {
  if (!rows.value.length) {
    alert('No data to print. Please generate the report first.')
    return
  }
  window.print()
}

async function loadDepartments() {
  if (!companyId.value && !branchId.value) return
  try {
    let endpoint = `/departments/company/${companyId.value}`
    if (branchId.value) endpoint = `/departments/branch/${branchId.value}`

    const res = await apiFetch(endpoint)
    if (res.ok) {
      const data = await res.json().catch(() => [])
      const list = Array.isArray(data) ? data : []
      departments.value = list.filter((d) => {
        const isCentral = d.isCentral ?? d.is_central
        return isCentral === 0 || isCentral === false
      })

      departments.value.sort((a, b) => a.name.localeCompare(b.name))
    }
  } catch (error) {
    console.error('Failed to load departments', error)
  }
}

function normalizeRow(raw) {
  const calendar = raw.calendar || raw.date || raw.trDate || raw.transactionDate || null
  const type = raw.type || (raw.productId || raw.product_id ? 'product' : 'item')
  const ingredient = raw.ingredient || raw.ingredientName || raw.name || raw.item || '-'
  const itemId = raw.itemId ?? raw.item_id ?? null
  const productId = raw.productId ?? raw.product_id ?? null
  const ingredientId = raw.ingredientId ?? raw.ingredient_id ?? null

  return {
    calendar,
    type,
    ingredient,
    ingredientId,
    itemId,
    productId,

    bbf: Number(raw.bbf ?? 0),
    storeRecv: Number(raw.storeRecv ?? raw.store_recv ?? 0),
    nonStock: Number(raw.nonStock ?? raw.non_stock ?? 0),
    production: Number(raw.production ?? 0),

    // Consumption module uses transIn1 as total Transfer Received (T)
    transInT: Number(raw.transInT ?? raw.transIn1 ?? raw.transferReceivedT ?? raw.transfer_received_t ?? 0),
    wastage: Number(raw.wastage ?? 0),

    salesManual: Number(raw.salesManual ?? raw.sales_manual ?? 0),
    salesSystem: Number(raw.salesSystem ?? raw.sales_system ?? 0),
    management: Number(raw.management ?? raw.mgmt ?? 0),
    room: Number(raw.room ?? 0),
    promotion: Number(raw.promotion ?? raw.promo ?? 0),
    companyCost: Number(raw.companyCost ?? raw.company_cost ?? raw.coCost ?? raw.co_cost ?? 0),
    spoilage: Number(raw.spoilage ?? 0),
    other: Number(raw.other ?? 0),
    // Consumption module uses transOut1 as total Transfer Issu (T)
    transOutT: Number(raw.transOutT ?? raw.transOut1 ?? raw.transferIssueT ?? raw.transfer_issue_t ?? 0),
    spoiledByItems: Number(raw.spoiledByItems ?? raw.spoiledItem ?? raw.spoiled_item ?? 0),
    otherCatering: Number(raw.otherCatering ?? raw.catering ?? 0),

    // computed
    totalOnHand: 0,
    totalActive: 0,
    balance: 0,
    ending: Number(raw.ending ?? 0),
    deference: 0,
  }
}

function computeDerived(r) {
  // Total On Hand = BBF + R.S + R.N.S ± production (subtract if item) + Transfer Received (T) - Wastage
  const productionTerm =
    String(r.type || '').toLowerCase() === 'item' ? -r.production : r.production
  const totalOnHand = (r.bbf + r.storeRecv + r.nonStock + productionTerm + r.transInT - r.wastage)

  // Total Active = sum of consumption fields + transfer issue (T) + spoiled by items + other/catering
  const totalActive =
    r.salesManual +
    r.salesSystem +
    r.management +
    r.room +
    r.promotion +
    r.companyCost +
    r.spoilage +
    r.other +
    r.transOutT +
    r.spoiledByItems +
    r.otherCatering

  const balance = totalOnHand - totalActive
  const deference = (Number(r.ending) || 0) - balance

  return {
    ...r,
    totalOnHand,
    totalActive,
    balance,
    deference,
  }
}

function groupKey(r) {
  const id = r.ingredientId ?? r.itemId ?? r.productId ?? r.ingredient
  return `${r.type}:${String(id)}`
}

function aggregateSummary(list, startDate, endDate) {
  const start = String(startDate || '')
  const end = String(endDate || startDate || '')

  // Group by ingredient (type+id/name).
  const byKey = new Map()
  for (const r0 of list) {
    const r = normalizeRow(r0)
    const key = groupKey(r)
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key).push(r)
  }

  const out = []
  for (const arr of byKey.values()) {
    // Sort by calendar (YYYY-MM-DD) when possible.
    const sorted = [...arr].sort((a, b) => String(a.calendar || '').localeCompare(String(b.calendar || '')))

    // BBF: use only the start date BBF (not summed).
    const startRow = sorted.find(x => String(x.calendar || '') === start) || sorted[0]
    const bbf = Number(startRow?.bbf ?? 0)

    // Ending: use only the end date Ending (not summed).
    const endRow = sorted.slice().reverse().find(x => String(x.calendar || '') === end) || sorted[sorted.length - 1]
    const ending = Number(endRow?.ending ?? 0)

    // Sum all other fields across the whole range list.
    const base = {
      calendar: null,
      type: sorted[0]?.type || 'item',
      ingredient: sorted[0]?.ingredient || '-',
      ingredientId: sorted[0]?.ingredientId ?? null,
      itemId: sorted[0]?.itemId ?? null,
      productId: sorted[0]?.productId ?? null,

      bbf,
      storeRecv: 0,
      nonStock: 0,
      production: 0,
      transInT: 0,
      wastage: 0,
      salesManual: 0,
      salesSystem: 0,
      management: 0,
      room: 0,
      promotion: 0,
      companyCost: 0,
      spoilage: 0,
      other: 0,
      transOutT: 0,
      spoiledByItems: 0,
      otherCatering: 0,
      ending,
    }

    for (const r of sorted) {
      base.storeRecv += Number(r.storeRecv) || 0
      base.nonStock += Number(r.nonStock) || 0
      base.production += Number(r.production) || 0
      base.transInT += Number(r.transInT) || 0
      base.wastage += Number(r.wastage) || 0
      base.salesManual += Number(r.salesManual) || 0
      base.salesSystem += Number(r.salesSystem) || 0
      base.management += Number(r.management) || 0
      base.room += Number(r.room) || 0
      base.promotion += Number(r.promotion) || 0
      base.companyCost += Number(r.companyCost) || 0
      base.spoilage += Number(r.spoilage) || 0
      base.other += Number(r.other) || 0
      base.transOutT += Number(r.transOutT) || 0
      base.spoiledByItems += Number(r.spoiledByItems) || 0
      base.otherCatering += Number(r.otherCatering) || 0
    }

    out.push(computeDerived(base))
  }

  // Optional item filter on UI (case-insensitive contains)
  const term = String(filters.value.searchItem || '').trim().toLowerCase()
  const filtered = term ? out.filter(r => String(r.ingredient || '').toLowerCase().includes(term)) : out

  return filtered.sort((a, b) => String(a.ingredient || '').localeCompare(String(b.ingredient || '')))
}

async function generateReport() {
  if (!filters.value.startDate) return
  if (filters.value.departmentId === null || filters.value.departmentId === undefined || String(filters.value.departmentId).trim() === '') {
    alert('please select department to continue')
    return
  }
  loading.value = true
  try {
    const start = String(filters.value.startDate || '')
    const end = String(filters.value.endDate || filters.value.startDate || '')
    if (!start) return

    const query = new URLSearchParams()
    if (branchId.value) query.append('branchId', String(branchId.value))
    else if (companyId.value) query.append('companyId', String(companyId.value))
    query.append('startDate', start)
    if (filters.value.endDate) query.append('endDate', end)
    query.append('departmentId', String(filters.value.departmentId))

    const res = await apiFetch(`/consumption-report?${query.toString()}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch consumption report')
    }

    const payload = await res.json().catch(() => ({ hasData: false, rows: [] }))
    const fetched = Array.isArray(payload?.rows) ? payload.rows : []

    if (filters.value.reportType === 'Detail') {
      const normalized = fetched.map(normalizeRow).map(computeDerived)
      const term = String(filters.value.searchItem || '').trim().toLowerCase()
      const filtered = term ? normalized.filter(r => String(r.ingredient || '').toLowerCase().includes(term)) : normalized
      rows.value = filtered.sort((a, b) => {
        const dc = String(a.calendar || '').localeCompare(String(b.calendar || ''))
        if (dc !== 0) return dc
        return String(a.ingredient || '').localeCompare(String(b.ingredient || ''))
      })
    } else {
      rows.value = aggregateSummary(fetched, start, end)
    }
  } catch (error) {
    console.error('Failed to fetch consumption report', error)
    alert(error.message || 'Failed to fetch consumption report')
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  filters.value.startDate = ""
  loadDepartments()
})
</script>

<style scoped>
.consumption-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th,
td {
  padding: 4px 6px;
  text-align: center;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.consumption-table th:nth-child(1),
.consumption-table td:nth-child(1) {
  text-align: center;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.sticky-ingredient {
  position: sticky;
  left: 0;
  z-index: 22;
  background: #ffffff;
}

thead .sticky-ingredient {
  background: #f1f5f9;
}

.print-short {
  display: none;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(aside),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  /* When the app uses a fixed sidebar, main content has a left margin on desktop.
     Remove it for print so the report uses the full page width. */
  :global(.md\:ml-\[150px\]) {
    margin-left: 0 !important;
  }

  .overflow-auto {
    overflow: visible !important;
    min-height: auto !important;
    max-height: none !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .print-table {
    width: 100% !important;
    min-width: 0 !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    table-layout: auto !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 1px 2px !important;
    text-align: left !important;
    color: #000 !important;
    font-size: 6.8px !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
    line-height: 1.15 !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-size: 6.2px !important;
    text-align: center !important;
    position: static !important;
    top: auto !important;
    white-space: normal !important;
    overflow: visible !important;
  }

  .print-table thead {
    position: static !important;
  }

  .print-table thead tr,
  .print-table tbody tr {
    page-break-inside: avoid !important;
  }

  .sticky-ingredient {
    position: static !important;
    left: auto !important;
    z-index: auto !important;
    background: transparent !important;
    /* Override Tailwind min-w-[120px] for print so the Item column isn't fixed */
    min-width: 0 !important;
    width: auto !important;
    max-width: none !important;
  }

  /* Prevent the Item column header/cells from breaking into vertical characters */
  .print-table th.sticky-ingredient,
  .print-table td.sticky-ingredient {
    white-space: nowrap !important;
    word-break: normal !important;
    overflow-wrap: normal !important;
  }

  .print-long {
    display: none !important;
  }

  .print-short {
    display: inline !important;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title-wrap {
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-company-name-title {
    font-size: 16px;
    text-align: center;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}
</style>

